import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown, faAngleUp, faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Link, Text,
} from '@domain-group/suit-theme'
import { useSetState } from '@domain-group/react-hooks'

import useProviderPackageItems from '@hooks/useProviderPackageItems'

import type { ProviderPackageModel } from '@models/providerPackage'

type DefaultState = {
  expanded: boolean,
  loading: boolean,
}

export type SetState = (state: Partial<DefaultState>) => void

const defaultState = {
  expanded: false,
  loading: false,
}

interface ProviderPackageProps {
  callbacks: {
    selectPackage: (state: SetState) => void,
  },
  providerPackage: ProviderPackageModel,
}

const ProviderPackage = (props: ProviderPackageProps) => {
  const {
    callbacks: {
      selectPackage,
    },
    providerPackage: { id, price, title },
  } = props

  const [state, setState] = useSetState(defaultState)
  const { expanded, loading } = state

  const providerPackageItemsPayload = useProviderPackageItems({
    filters: {
      provider_package_id: id,
    },
  })

  const {
    filteredProviderPackageItemsCount,
    filteredProviderPackageItems,
    hasFilteredProviderPackageItems,
  } = providerPackageItemsPayload

  return (
    <Box
      alignItems="center"
      border="1px dashed"
      borderColor="lineColor"
      borderRadius="5px"
      flexDirection="column"
      marginTop="medium"
      padding="large"
    >
      <Box>
        <Box justifyContent="center" flexDirection="column" width="calc(100% - 166px)">
          <Text fontSize="small" lineHeight="1.3">
            {title}
          </Text>

          <Link
            disabled={!hasFilteredProviderPackageItems}
            onClick={() => setState({ expanded: !expanded })}
            textProps={{ fontSize: 'small', marginTop: 'medium' }}
          >
            {hasFilteredProviderPackageItems && (
              <FontAwesomeIcon icon={expanded ? faAngleUp : faAngleDown} style={{ marginRight: 8 }} />
            )}

            {filteredProviderPackageItemsCount === 1 ? '1 Item' : `${filteredProviderPackageItemsCount} Items`}
          </Link>
        </Box>

        <Box alignItems="center" justifyContent="flex-end" marginLeft="auto" width={150}>
          <Text color="bodyFontLightColor" fontSize="small" marginRight="large">
            {formatMoney(price)}
          </Text>

          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            loading={loading}
            onClick={() => {
              setState({ loading: true })
              selectPackage(setState)
            }}
            size="small"
            // @ts-expect-error TS2322
            width="auto"
          >
            Add
          </Button>
        </Box>
      </Box>

      {expanded && (
        <Box
          backgroundColor="whiteGrey"
          borderRadius="5px"
          flexDirection="column"
          marginTop="medium"
          padding="large"
          paddingTop="medium"
        >
          {filteredProviderPackageItems.map((packageItem) => {
            const {
              id, options_string, quantity, title: itemTitle,
            } = packageItem
            return (
              <Box key={id} lineHeight="1.3">
                <Text fontSize="xsmall" marginTop="medium">
                  {quantity} x {itemTitle}
                </Text>

                <Text color="bodyFontLightColor" fontSize="xsmall" marginLeft="small" marginTop="medium">
                  {options_string}
                </Text>
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default ProviderPackage
